import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import * as fixedAssetService from '../services/fixedAssetService';
import { getLocalDateString } from '../../../shared/utils/dateUtils';

const FixedAssetForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    assetCode: '',
    name: '',
    categoryId: '',
    purchaseDate: getLocalDateString(),
    purchaseCost: '',
    salvageValue: '0',
    usefulLifeYears: '',
    depreciationMethod: 'Straight Line',
    depreciationRate: '',
    status: 'Active',
    location: '',
    serialNumber: '',
    description: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // Fetch categories for dropdown
      const catRes = await fixedAssetService.getCategories();
      if (catRes.success) {
        setCategories(catRes.data.filter(c => c.isActive));
      }

      if (isEdit) {
        const res = await fixedAssetService.getAssetById(id);
        if (res.success) {
          const asset = res.data;
          setFormData({
            assetCode: asset.assetCode || '',
            name: asset.name || '',
            categoryId: asset.categoryId || '',
            purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '',
            purchaseCost: asset.purchaseCost || '',
            salvageValue: asset.salvageValue || '0',
            usefulLifeYears: asset.usefulLifeYears || '',
            depreciationMethod: asset.depreciationMethod || 'Straight Line',
            depreciationRate: asset.depreciationRate || '',
            status: asset.status || 'Active',
            location: asset.location || '',
            serialNumber: asset.serialNumber || '',
            description: asset.description || ''
          });
        }
      }
    } catch (error) {
      console.error('Failed to load data', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      
      // Auto-populate based on category selection
      if (name === 'categoryId') {
        const selectedCat = categories.find(c => c.id === value);
        if (selectedCat) {
          newData.depreciationMethod = selectedCat.depreciationMethod;
          newData.depreciationRate = selectedCat.defaultDepreciationRate || '';
          newData.usefulLifeYears = selectedCat.usefulLifeYears || '';
        }
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (isEdit) {
        await fixedAssetService.updateAsset(id, formData);
      } else {
        await fixedAssetService.createAsset(formData);
      }
      navigate('/fixed-assets');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save asset');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/fixed-assets')}>
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Fixed Asset' : 'New Fixed Asset'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Asset Code *"
                name="assetCode"
                value={formData.assetCode}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Asset Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Category *"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Purchase Date *"
                type="date"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Purchase Cost *"
                type="number"
                name="purchaseCost"
                inputProps={{ min: 0, step: "0.01" }}
                value={formData.purchaseCost}
                onChange={handleChange}
                required
                disabled={isEdit} // Don't allow changing initial cost easily
                helperText={isEdit ? "Cannot edit purchase cost after creation" : ""}
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Salvage Value"
                type="number"
                name="salvageValue"
                inputProps={{ min: 0, step: "0.01" }}
                value={formData.salvageValue}
                onChange={handleChange}
              />
            </Grid>

            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Depreciation Method"
                name="depreciationMethod"
                value={formData.depreciationMethod}
                onChange={handleChange}
              >
                <MenuItem value="Straight Line">Straight Line</MenuItem>
                <MenuItem value="Declining Balance">Declining Balance</MenuItem>
                <MenuItem value="None">None</MenuItem>
              </TextField>
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Depreciation Rate (%)"
                type="number"
                name="depreciationRate"
                inputProps={{ min: 0, max: 100, step: "0.01" }}
                value={formData.depreciationRate}
                onChange={handleChange}
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Useful Life (Years)"
                type="number"
                name="usefulLifeYears"
                inputProps={{ min: 1 }}
                value={formData.usefulLifeYears}
                onChange={handleChange}
              />
            </Grid>

            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Serial Number"
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleChange}
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </Grid>
            <Grid  size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Disposed">Disposed</MenuItem>
                <MenuItem value="Maintenance">Maintenance</MenuItem>
                <MenuItem value="Sold">Sold</MenuItem>
              </TextField>
            </Grid>
            
            <Grid  size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>

            <Grid  size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => navigate('/fixed-assets')} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  sx={{ bgcolor: '#4ECDC4', '&:hover': { bgcolor: '#3BA89F' } }}
                >
                  {saving ? 'Saving...' : 'Save Asset'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default FixedAssetForm;
